/**
 * The job page: its tabs, the Overview, the checklists and the small cards
 * beside them (ADR 0325); see index.ts for the house style.
 */
export const job: Readonly<Record<string, string>> = {
  // The tabs across the top of a job (CARDS in JobDetail)
  Overview: "Resumen",
  Estimate: "Presupuesto",
  Payments: "Pagos",
  "Insurance Estimate": "Presupuesto de seguro",
  Notes: "Notas",
  Activity: "Actividad",
  Invoices: "Facturas",
  Commissions: "Comisiones",
  Events: "Eventos",
  Docs: "Documentos",
  Photos: "Fotos",
  Conversations: "Conversaciones",
  Emails: "Correos",
  Texts: "Mensajes",
  Partners: "Socios",
  "Roof Drawing": "Dibujo del techo",
  "Roof Measurements": "Medidas del techo",
  Inspections: "Inspecciones",
  Todo: "Pendientes",
  "Planned, not wired up in v1.": "Planeado, todavía no conectado en v1.",
  "Drawing a roof in Roof Board is not built. The Draw Roof step, and Measure the roof on the Roof Measurements card, hand the address across to iRoofing or QuickMeasure.":
    "Dibujar un techo dentro de Roof Board no está construido. El paso Dibujar techo y Medir el techo en la tarjeta Medidas del techo pasan la dirección a iRoofing o QuickMeasure.",

  // The head of the job
  Job: "Trabajo",
  "Test lead": "Prospecto de prueba",
  "this customer": "este cliente",
  "No job address yet": "Todavía sin dirección del trabajo",
  "Everything this customer has with us":
    "Todo lo que este cliente tiene con nosotros",
  "Open customer file ({n} jobs)":
    "Abrir expediente del cliente ({n} trabajos)",
  "Open customer file": "Abrir expediente del cliente",
  "Leave this job and go back to the list you came from":
    "Salir de este trabajo y volver a la lista de donde venías",
  "Back to the job list": "Volver a la lista de trabajos",
  "Job not found": "Trabajo no encontrado",
  "It may have been removed.": "Puede que se haya eliminado.",

  // Warnings and small modals on the job page
  "The box is ticked, and the balance still counts it as owed.":
    "La casilla está marcada y el saldo sigue contándolo como pendiente.",
  "Record the payment": "Registrar el pago",
  "Not now": "Ahora no",
  "Record check or cash": "Registrar cheque o efectivo",
  Close: "Cerrar",
  "Could not read this job's payment plan, so a payment cannot be recorded yet: {error}":
    "No se pudo leer el plan de pagos de este trabajo, así que todavía no se puede registrar un pago: {error}",
  "Reading this job's payment plan…":
    "Leyendo el plan de pagos de este trabajo…",
  "Check the payment schedule matches what was promised.":
    "Revisa que el calendario de pagos coincida con lo prometido.",
  "This is fine": "Está bien así",
  "The box is unticked, but the check stays paid: Mercury has seen it deposited. Money In is where a bank match is undone.":
    "La casilla se desmarcó, pero el cheque sigue pagado: Mercury ya lo vio depositado. En Dinero entrante es donde se deshace una coincidencia bancaria.",
  "The walls were saved, but the report was not filed under Docs ({docs}) and the roof was not saved ({roof}). Add the roof on the Roof Measurements card.":
    "Las paredes se guardaron, pero el reporte no se archivó en Documentos ({docs}) y el techo no se guardó ({roof}). Agrega el techo en la tarjeta Medidas del techo.",
  "The walls were saved, but the roof was not: {roof}. Add the roof on the Roof Measurements card.":
    "Las paredes se guardaron, pero el techo no: {roof}. Agrega el techo en la tarjeta Medidas del techo.",
  "The walls were saved, but the report was not filed under Docs: {docs}.":
    "Las paredes se guardaron, pero el reporte no se archivó en Documentos: {docs}.",
  'Remove "{name}"?': "¿Quitar “{name}”?",
  "these walls": "estas paredes",
  "this layout": "este plano",

  // Overview: Customer Info
  "Customer Info": "Datos del cliente",
  "First name": "Nombre",
  "Last name": "Apellido",
  Company: "Empresa",
  Cell: "Celular",
  "Alternate phone": "Teléfono alterno",
  "Whose? e.g. spouse": "¿De quién? p. ej. cónyuge",
  Email: "Email",
  "Mailing Address": "Dirección postal",
  Jobs: "Trabajos",
  "1 job — view customer →": "1 trabajo — ver cliente →",
  "{n} jobs — view customer →": "{n} trabajos — ver cliente →",
  "These details are shared across every job for this customer.":
    "Estos datos se comparten entre todos los trabajos de este cliente.",

  // Overview: Job Info
  "Job Info": "Datos del trabajo",
  "Job Address": "Dirección del trabajo",
  City: "Ciudad",
  State: "Estado",
  Zip: "Código postal",
  "Lead Source": "Origen del prospecto",
  "Job Number": "Número de trabajo",
  "Job Name": "Nombre del trabajo",
  "Date Created": "Fecha de creación",
  "Created By": "Creado por",
  "Job Type": "Tipo de trabajo",
  Residential: "Residencial",
  Commercial: "Comercial",
  "Bid Type": "Tipo de oferta",
  "Lead Status": "Estado del prospecto",
  "Auto ({stage})": "Automático ({stage})",
  "Next Follow-up": "Próximo seguimiento",
  "Next follow-up date": "Fecha del próximo seguimiento",
  "Last Contact": "Último contacto",
  "No call, text or email logged yet":
    "Todavía no hay llamada, mensaje ni correo registrado",
  "Sales Rep": "Vendedor",
  "Alt Rep": "Vendedor alterno",
  Inspection: "Inspección",
  "Pick an inspector or crew…": "Elige un inspector o cuadrilla…",
  "Second inspector": "Segundo inspector",
  "Anyone else going…": "¿Alguien más va?…",
  "Project Coordinator": "Coordinador de proyecto",
  "Project Manager": "Gerente de proyecto",
  "Claim #": "Nº de reclamo",
  "Roof Date": "Fecha del techo",
  "Inspection Notes": "Notas de inspección",
  "(no longer here)": "(ya no está aquí)",
  "Kept out of the sold list, the payouts and lifetime value":
    "Fuera de la lista de vendidos, los pagos a cuadrillas y el valor de por vida",
  "Mark this as practice, so it never lands in the numbers":
    "Marcar como práctica, para que nunca entre en las cifras",
  "Practice only — never counted as a sale, and hidden from everyone without the tester role":
    "Solo práctica — nunca cuenta como venta y está oculto para quien no tenga el rol de probador",
  "A real job. Turning this on hides it from everyone without the tester role":
    "Un trabajo real. Al activarlo se oculta para quien no tenga el rol de probador",
  "Next Step": "Siguiente paso",
  "Work the approved checklist": "Trabajar la lista aprobada",
  "Change bid type?": "¿Cambiar el tipo de oferta?",
  "This rebuilds the checklist for {bid} and resets checklist progress.":
    "Esto reconstruye la lista para {bid} y reinicia su avance.",
  "Change bid type": "Cambiar tipo de oferta",
  Cancel: "Cancelar",

  // Bid types (capitalise(bid_type))
  Repair: "Reparación",
  Retail: "Venta directa",
  Insurance: "Seguro",
  "{bid} Bid": "Oferta de {bid}",

  // Stages (STAGE_LABEL in packages/core)
  "New Lead": "Nuevo prospecto",
  Contacted: "Contactado",
  "Inspection Scheduled": "Inspección programada",
  Inspected: "Inspeccionado",
  "Proposal Sent": "Propuesta enviada",
  Signed: "Firmado",
  "In Production": "En producción",
  Complete: "Completado",
  Closed: "Cerrado",
  Lost: "Perdido",

  // Deleting a job
  "this job": "este trabajo",
  "Delete job #{number} for {name}?":
    "¿Eliminar el trabajo #{number} de {name}?",
  "This removes its estimates, orders and expenses too, and cannot be undone.":
    "Esto elimina también sus presupuestos, órdenes y gastos, y no se puede deshacer.",
  "It is a test lead, so {name} goes with it — along with any calls and texts on it — unless they have other work.":
    "Es un prospecto de prueba, así que {name} se va con él — junto con sus llamadas y mensajes — a menos que tenga otros trabajos.",
  "Deleting…": "Eliminando…",
  "Delete this test lead": "Eliminar este prospecto de prueba",
  "Delete this job": "Eliminar este trabajo",
  "A test lead always goes, however far it was walked through, and takes its customer off the lists with it.":
    "Un prospecto de prueba siempre se elimina, sin importar hasta dónde haya avanzado, y se lleva a su cliente de las listas.",
  "Only while nothing has been signed, sent, ordered or paid. After that, mark it lost instead.":
    "Solo mientras no se haya firmado, enviado, pedido ni pagado nada. Después de eso, márcalo como perdido.",

  // Lost
  "Lost — {reason}": "Perdido — {reason}",
  "no reason recorded": "sin motivo registrado",
  "Reopen job": "Reabrir trabajo",
  "Mark job lost": "Marcar trabajo perdido",
  "Mark this job lost": "Marcar este trabajo como perdido",
  "Nothing is deleted. The job keeps its history and drops off the boards — you can reopen it later.":
    "No se elimina nada. El trabajo conserva su historial y sale de los tableros — se puede reabrir después.",
  "Mark lost": "Marcar perdido",
  Reason: "Motivo",
  "Pick a reason…": "Elige un motivo…",
  "Required — this is what the lost report counts.":
    "Obligatorio — esto es lo que cuenta el informe de perdidos.",
  "Note (optional)": "Nota (opcional)",
  "Anything worth remembering about why":
    "Algo que valga la pena recordar del porqué",
  // The lost reasons (lost_reasons rows)
  "Not Interested": "No le interesa",
  "Not Responding": "No responde",
  "Not Responding - Estimate Sent": "No responde - Presupuesto enviado",
  "Under Contract with Other Company": "Bajo contrato con otra empresa",
  Unqualified: "No califica",
  "No Damage": "Sin daños",
  "Maybe Interested in Future": "Quizá le interese en el futuro",
  "Disliked Us": "No le gustamos",
  Blacklisted: "En lista negra",
  Duplicate: "Duplicado",
  SPAM: "SPAM",
  "SCAM ALERT": "ALERTA DE ESTAFA",
  Dead: "Muerto",

  // Selections
  "Which colour did the customer choose?": "¿Qué color eligió el cliente?",
  Selections: "Selecciones",
  "Other colours": "Otros colores",
  "From the Sends email": "Del correo de envío",
  Colour: "Color",
  "Not chosen yet": "Todavía sin elegir",
  "Other…": "Otro…",
  "Chosen by hand.": "Elegido a mano.",
  "Use {colour} from the job instead": "Usar mejor {colour} del trabajo",
  Clear: "Borrar",
  "The material order says {colour}; the Sends email says {sends}.":
    "La orden de materiales dice {colour}; el correo de envío dice {sends}.",
  // COLOUR_SOURCE_LABEL in packages/core
  "chosen by hand": "elegido a mano",
  "from the material order": "de la orden de materiales",
  "from the Sends email": "del correo de envío",
  "from the estimate": "del presupuesto",

  // What the customer sent
  "What the customer sent": "Lo que envió el cliente",
  "Through {link} on {date}. Their own words — the job above is yours to edit.":
    "Por {link} el {date}. Sus propias palabras — el trabajo de arriba es tuyo para editar.",
  "{slug}'s link": "el enlace de {slug}",
  "the link": "el enlace",
  Wants: "Quiere",
  Free: "Disponible",
  "Heard about us": "Supo de nosotros",
  "— filed as {source}": "— registrado como {source}",
  "Sent by the customer": "Enviada por el cliente",
  "In CompanyCam, marked From customer.":
    "En CompanyCam, marcadas Del cliente.",
  "{n} of {total} in CompanyCam — the rest follow once the project is made.":
    "{n} de {total} en CompanyCam — el resto sigue cuando se cree el proyecto.",

  // Activity
  "No activity yet": "Todavía sin actividad",
  "Completed steps, status changes, and notes will show here — with who and when.":
    "Los pasos completados, los cambios de estado y las notas aparecerán aquí — con quién y cuándo.",
  DONE: "HECHO",
  STAGE: "ETAPA",
  NOTE: "NOTA",
  "Status → {stage}": "Estado → {stage}",

  // Materials check
  Message: "Mensaje",
  Dismiss: "Descartar",
  "Looks like the material order doesn’t show what was asked for.":
    "Parece que la orden de materiales no muestra lo que se pidió.",
  "The material order doesn’t show what was asked for.":
    "La orden de materiales no muestra lo que se pidió.",
  "Draft order": "Orden en borrador",
  "Placed order": "Orden enviada",

  // Register Warranty tiers
  "{name} ({n} of {of} parts{rate})": "{name} ({n} de {of} piezas{rate})",

  // The roof offered off a Hover report
  "Also save the roof measurements": "Guardar también las medidas del techo",
  "read off this report ({count} read{pitch}) onto the Roof Measurements card.":
    "leídas de este reporte ({count} leídas{pitch}) en la tarjeta Medidas del techo.",
  ", {pitch} pitch": ", pendiente {pitch}",
  "Left unticked because this job already has {what}. A roof estimate adds up every one on the job, so saving this roof too would count it twice. Tick the box only if it is a roof not measured yet.":
    "Sin marcar porque este trabajo ya tiene {what}. Un presupuesto de techo suma todas las del trabajo, así que guardar este techo también lo contaría dos veces. Marca la casilla solo si es un techo que todavía no se ha medido.",
  "a roof measurement": "una medida de techo",
  "{n} roof measurements": "{n} medidas de techo",
  "Roof pitch": "Pendiente del techo",
  Pitch: "Pendiente",
  "Pick the roof pitch, or untick the box to save the walls alone.":
    "Elige la pendiente del techo, o desmarca la casilla para guardar solo las paredes.",
  "Roof waste pct": "Desperdicio del techo %",
  "Hover recommends none, so this is yours to pick.":
    "Hover no recomienda ninguno, así que lo eliges tú.",

  // The checklists
  "Prospect Checklist": "Lista del prospecto",
  "↻ Reassign Sales Rep": "↻ Reasignar vendedor",
  "Approved Checklist": "Lista aprobada",
  "Materials Handled Internally": "Materiales manejados internamente",
  "Crew Is Providing Material": "La cuadrilla pone el material",
  "Work Orders Handled Internally": "Órdenes de trabajo manejadas internamente",
  "Material came off our own shelf — the Material step is not on this list.":
    "El material salió de nuestro propio almacén — el paso Material no está en esta lista.",
  "The crew is bringing the material — the Material step is not on this list.":
    "La cuadrilla trae el material — el paso Material no está en esta lista.",
  "No work orders on this job — tick the two work steps yourself when the work is booked and when it is finished.":
    "Este trabajo no tiene órdenes de trabajo — marca tú mismo los dos pasos de obra cuando se programe y cuando termine.",
  Production: "Producción",
  "Follow-up": "Seguimiento",
  Financing: "Financiamiento",
  "N/A": "N/A",
  "Sends email": "Correo de envío",
  "✉ Night-before email to the customer":
    "✉ Correo de la noche anterior al cliente",
  "Undo the handoff? This clears the approved checklist.":
    "¿Deshacer la entrega? Esto borra la lista aprobada.",
  "Google Calendar is out of step with this inspection.":
    "Google Calendar no coincide con esta inspección.",
  "Re-save the inspection to try again.":
    "Vuelve a guardar la inspección para intentarlo de nuevo.",
  "The handoff went, but its email was not kept on the job. Paste it in with the Sends email button beside the Sends step.":
    "La entrega se hizo, pero su correo no se guardó en el trabajo. Pégalo con el botón Correo de envío junto al paso Envío.",
  // The step names (checklist_template_items rows)
  Verified: "Verificado",
  "Hot Lead": "Prospecto caliente",
  "Roof Measurement": "Medida del techo",
  "Draw Roof": "Dibujar techo",
  "Estimate Sent": "Presupuesto enviado",
  "Signed Contract": "Contrato firmado",
  Sends: "Envío",
  "Pre-Contract": "Precontrato",
  Contract: "Contrato",
  "Docs Signed": "Documentos firmados",
  Approved: "Aprobado",
  "Claim Filed": "Reclamo presentado",
  "Adjuster Meeting": "Reunión con el ajustador",
  "Upload Insurance Scope": "Subir alcance del seguro",
  Material: "Material",
  Permit: "Permiso",
  "Notify City Inspector": "Avisar al inspector de la ciudad",
  "Project Manager Introduction": "Presentación del gerente de proyecto",
  "All Work Scheduled": "Toda la obra programada",
  "All Work Complete": "Toda la obra completada",
  "Roof Scheduled": "Techo programado",
  "Roof Complete": "Techo completado",
  "Work Orders Scheduled": "Órdenes de trabajo programadas",
  "Trades Complete": "Oficios completados",
  "QA Inspection": "Inspección de calidad",
  "First Check": "Primer cheque",
  "First Check Submitted": "Primer cheque entregado",
  "Final Check": "Cheque final",
  "Final Check Submitted": "Cheque final entregado",
  "Paid In Full": "Pagado por completo",
  "Register Warranty": "Registrar garantía",
  "Register Rewards": "Registrar recompensas",
  "Project Recap Email": "Correo de resumen del proyecto",
  "Five Star Review": "Reseña de cinco estrellas",
  "Verify Trades": "Verificar oficios",
  "Verify Each Rep": "Verificar cada vendedor",
  "Verify All Expenses Added": "Verificar que se agregaron todos los gastos",
  "Verify All Expenses Reimbursed":
    "Verificar que se reembolsaron todos los gastos",
  "Accounting Review": "Revisión contable",

  // Send to Kelsey
  someone: "alguien",
  close: "cierre",
  "Sent to {owner} by {by} on {when}": "Enviado a {owner} por {by} el {when}",
  "Nobody is set to receive finished jobs. An admin sets it in Tools → Notifications.":
    "Nadie está asignado para recibir los trabajos terminados. Un administrador lo asigna en Herramientas → Notificaciones.",
  "Send to {name}": "Enviar a {name}",

  // Reassign
  "Reassign sales rep": "Reasignar vendedor",
  Save: "Guardar",

  // Verify
  Verify: "Verificar",
  "Saving…": "Guardando…",
  Submit: "Enviar",
  "Only if the customer is a business": "Solo si el cliente es una empresa",
  "A last name, or a company name — one of them has to be filled in.":
    "Un apellido o el nombre de una empresa — hay que llenar uno de los dos.",
  "Whose is the alternate?": "¿De quién es el alterno?",
  "Spouse, work, mother": "Cónyuge, trabajo, madre",
  Source: "Origen",
  "Bid type": "Tipo de oferta",
  "Changing the bid type rebuilds this job's checklists.":
    "Cambiar el tipo de oferta reconstruye las listas de este trabajo.",
  "Claim number": "Número de reclamo",
  Note: "Nota",
  // Lead sources (lead_sources rows)
  Organic: "Orgánico",
  Website: "Sitio web",
  "Yard Sign": "Letrero en el jardín",
  Truck: "Camioneta",
  "Rep Link": "Enlace del vendedor",

  // Signed Contract
  "Attach the signed copy. Electronic signing will tick this step on its own once it is built.":
    "Adjunta la copia firmada. La firma electrónica marcará este paso sola cuando esté construida.",
  "Mark signed": "Marcar firmado",
  "Choose a different file": "Elegir otro archivo",
  "Upload signed contract": "Subir contrato firmado",
  "Already attached: {name}": "Ya adjunto: {name}",
  "PDF or photo. Optional — you can mark it signed without one.":
    "PDF o foto. Opcional — puedes marcarlo firmado sin uno.",
  "Date signed": "Fecha de firma",

  // Schedule inspection
  "Schedule inspection": "Programar inspección",
  "Sets the date and time and says who is going. It goes on your Google Calendar and invites them.":
    "Fija la fecha y la hora y dice quién va. Se agrega a tu Google Calendar y los invita.",

  // The Sends email
  "The Sends email was not saved. Try again.":
    "El correo de envío no se guardó. Inténtalo de nuevo.",
  "The handoff to production, as it went.":
    "La entrega a producción, tal como se envió.",
  "From the job's email history. No copy was kept on the job yet.":
    "Del historial de correos del trabajo. Todavía no se guardó una copia en el trabajo.",
  "No copy was kept of this job's handoff. Paste it here from Gmail so it stays with the job.":
    "No se guardó copia de la entrega de este trabajo. Pégala aquí desde Gmail para que quede con el trabajo.",
  Edit: "Editar",
  "Keep it on the job": "Guardarlo en el trabajo",
  Done: "Listo",

  // The handoff
  "Hold the payment reminders while the check is on its way?":
    "¿Pausar los recordatorios de pago mientras llega el cheque?",
  "Yes, hold them for {n} days": "Sí, pausarlos {n} días",
  "No, keep sending them": "No, seguir enviándolos",
  "Mark as sent": "Marcar como enviado",
  "Build the email again from your answers?":
    "¿Armar el correo de nuevo con tus respuestas?",
  "Your changes to the email will be lost.":
    "Se perderán tus cambios al correo.",
  "Send handoff to Lysa": "Enviar entrega a Lysa",
  "Sending it opens the approved checklist.":
    "Al enviarlo se abre la lista aprobada.",
  "Send and mark sent": "Enviar y marcar enviado",
  Back: "Atrás",
  "Hand off to production": "Entregar a producción",
  "Scope, contract, signed date and deposit come from the job. Check the colour, and answer only what it cannot know.":
    "El alcance, el contrato, la fecha de firma y el depósito vienen del trabajo. Revisa el color y contesta solo lo que el trabajo no puede saber.",
  "Write the email": "Escribir el correo",
  "Reading the job…": "Leyendo el trabajo…",
  "When can the crew start?": "¿Cuándo puede empezar la cuadrilla?",
  "e.g. end of next week": "p. ej. a fines de la próxima semana",
  Needed: "Se necesita",
  "Not needed": "No se necesita",
  "Not sure": "No estoy seguro",
  "How are they paying the deposit?": "¿Cómo pagan el depósito?",
  "Not decided yet": "Todavía sin decidir",
  "Still deciding (one per line)": "Todavía por decidir (uno por línea)",
  "Anything else production should know?":
    "¿Algo más que producción deba saber?",
  "e.g. Jordan said it's in stock.":
    "p. ej. Jordan dijo que hay en existencia.",
  "Colour the customer chose — {option}":
    "Color que eligió el cliente — {option}",
  "Colour the customer chose": "Color que eligió el cliente",
  "e.g. Weathered Wood": "p. ej. Weathered Wood",
  // HANDOFF_PAY_METHODS in packages/core
  "ACH (bank transfer)": "ACH (transferencia bancaria)",
  "Credit card": "Tarjeta de crédito",
  Check: "Cheque",
  Cash: "Efectivo",

  // The trade strip under the two work steps
  "No work orders yet — add one and this ticks itself.":
    "Todavía no hay órdenes de trabajo — agrega una y esto se marca solo.",
  "Open the {trade} work order to mark it complete":
    "Abrir la orden de trabajo de {trade} para marcarla completada",
  "Open the {trade} work order": "Abrir la orden de trabajo de {trade}",
  Roofing: "Techado",
  "Metal Roofing": "Techado metálico",
  Siding: "Revestimiento",
  Gutters: "Canalones",
  "Other work": "Otro trabajo",
};
